import { useEffect, useState } from 'react';
import {
  COMPANY_ROLE_LABEL,
  ORDER_STATUS_LABEL,
  TOWED_KINDS,
  formatRub,
  formatMoney,
  topRole,
  vehicleTitle,
  type CrewRow,
  type FleetRow,
  type OrderStatusEventRow,
  type TrackPointRow,
} from 'shared';
import { api } from '../api';
import CountryFlag from '../CountryFlag';
import RouteLine from '../Route';
import Icon from '../Icon';
import Modal from '../Modal';
import TrackMap from '../TrackMap';
import CrewInvite from './CrewInvite';
import { haptic } from '../telegram';
import { useLiveRefresh } from '../realtime';
import { toast } from '../toast';

interface Props {
  companyId: string;
  /** Владелец видит форму найма и правит закрепления. */
  canHire: boolean;
}

/**
 * Экипаж компании: кто где и с чем.
 *
 * Строками, а не плитками. Плитки были рассчитаны на «посмотреть одного»,
 * а вопрос к этому экрану другой: пробежать глазами двадцать человек и найти
 * свободного. В строке всё на своих местах — имя, роль, машина, чем занят, —
 * и глаз идёт по столбцу, а не прыгает по карточкам.
 */
export default function Crew({ companyId, canHire }: Props) {
  const [items, setItems] = useState<CrewRow[]>([]);
  const [fleet, setFleet] = useState<FleetRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<CrewRow | null>(null);

  async function load() {
    try {
      const [c, f] = await Promise.all([api.crew(companyId), api.fleet(companyId)]);
      setItems(c.items);
      // Прицепы в подборе не участвуют: за руль прицепа не сажают.
      setFleet(f.items.filter((v) => !TOWED_KINDS.includes(v.kind) && v.active));
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  // Рейсы едут, пока экран открыт. Перечитываем при возврате фокуса
  // и раз в минуту: иначе «где машина» показывает вчерашнее.
  useLiveRefresh(() => void load());

  /**
   * Закрепить человека за машиной из его собственной строки.
   *
   * То же действие есть в карточке машины — и это не дублирование:
   * диспетчер думает то от людей («кому дать машину»), то от техники
   * («кого посадить на эту»). Заставлять его каждый раз идти в другой
   * список значит терять минуту на каждое назначение.
   */
  async function assign(userId: string, vehicleId: string) {
    try {
      // Сначала снимаем человека с прежней машины: одно закрепление на
      // водителя, иначе он числится сразу в двух рейсах.
      const previous = fleet.filter((v) => v.crew.some((c) => c.id === userId));
      for (const v of previous) {
        if (v.id === vehicleId) continue;
        await api.patchVehicle(v.id, {
          crew: v.crew.filter((c) => c.id !== userId).map((c) => c.id),
        });
      }

      if (vehicleId) {
        const target = fleet.find((v) => v.id === vehicleId);
        const next = [...new Set([...(target?.crew.map((c) => c.id) ?? []), userId])];
        await api.patchVehicle(vehicleId, { crew: next });
      }

      await load();
      toast.success(vehicleId ? 'Машина закреплена' : 'Закрепление снято');
    } catch (e) {
      toast.error((e as Error).message);
    }
  }

  if (loading && items.length === 0) return <div className="card">Загружаю…</div>;
  if (error) return <div className="empty">{error}</div>;

  const working = items.filter((c) => c.active);
  const off = items.filter((c) => !c.active);

  /** Какая машина закреплена за человеком. Источник — парк, а не экипаж. */
  const vehicleOf = (userId: string) =>
    fleet.find((v) => v.crew.some((c) => c.id === userId)) ?? null;

  return (
    <>
      {canHire && <CrewInvite companyId={companyId} onDone={() => void load()} />}

      {items.length === 0 && (
        <div className="empty">
          В компании пока никого нет. Заведите сотрудника или выдайте код
          приглашения.
        </div>
      )}

      {working.length > 0 && (
        <div className="crew-list">
          {working.map((c) => (
            <CrewLine
              key={c.userId}
              row={c}
              vehicle={vehicleOf(c.userId)}
              fleet={fleet}
              canManage={canHire}
              onAssign={(vehicleId) => void assign(c.userId, vehicleId)}
              onOpen={() => setOpen(c)}
            />
          ))}
        </div>
      )}

      {off.length > 0 && (
        <>
          <h3>Не работают ({off.length})</h3>
          <div className="crew-list">
            {off.map((c) => (
              <CrewLine
                key={c.userId}
                row={c}
                vehicle={vehicleOf(c.userId)}
                fleet={fleet}
                canManage={false}
                onAssign={() => {}}
                onOpen={() => setOpen(c)}
              />
            ))}
          </div>
        </>
      )}

      <Modal open={open !== null} onClose={() => setOpen(null)} title={open?.name}>
        {open && <CrewDetail row={open} vehicle={vehicleOf(open.userId)} />}
      </Modal>
    </>
  );
}

function CrewLine({
  row,
  vehicle,
  fleet,
  canManage,
  onAssign,
  onOpen,
}: {
  row: CrewRow;
  vehicle: FleetRow | null;
  fleet: FleetRow[];
  canManage: boolean;
  onAssign: (vehicleId: string) => void;
  onOpen: () => void;
}) {
  const t = row.trip;

  return (
    <div className={`crew-line ${row.active ? '' : 'crew-line--off'}`}>
      <button
        className="crew-line__who"
        onClick={() => {
          haptic();
          onOpen();
        }}
      >
        <strong>{row.name}</strong>
        <span className="meta">
          {COMPANY_ROLE_LABEL[topRole(row.roles)]}
          {row.roles.length > 1 ? ` +${row.roles.length - 1}` : ''}
          {!row.active ? ' · уволен' : ''}
        </span>
      </button>

      {/* Машина правится прямо в строке: закрепление — самое частое действие
          на этом экране, и ради него не должно открываться окно. */}
      <div className="crew-line__car">
        {canManage && row.canDrive ? (
          <select
            value={vehicle?.id ?? ''}
            onChange={(e) => onAssign(e.target.value)}
          >
            <option value="">Без машины</option>
            {fleet.map((v) => (
              <option key={v.id} value={v.id}>
                {v.plate}
                {v.crew.length > 0 && v.id !== vehicle?.id ? ' · занята' : ''}
              </option>
            ))}
          </select>
        ) : (
          <span className="meta">
            {vehicle
              ? vehicle.plate
              : row.canDrive
                ? 'без машины'
                : 'за руль не ставится'}
          </span>
        )}
      </div>

      <div className="crew-line__state meta">
        {t ? (
          <>
            <span className="badge badge--warn">{ORDER_STATUS_LABEL[t.status]}</span>{' '}
            <RouteLine from={t.fromAddress} to={t.toAddress} />
          </>
        ) : row.ready ? (
          `Свободен · ${row.ready.fromRegion} → ${row.ready.toRegion ?? 'куда угодно'}`
        ) : (
          'Свободен'
        )}
      </div>
    </div>
  );
}

/**
 * Что видно по нажатию: персональные данные, контакты и рейс целиком —
 * хроника статусов и машина на карте. Всё это уже отдают маршруты рейса,
 * потому что диспетчер компании и так его сторона.
 */
function CrewDetail({ row, vehicle }: { row: CrewRow; vehicle: FleetRow | null }) {
  const [history, setHistory] = useState<OrderStatusEventRow[]>([]);
  const [track, setTrack] = useState<TrackPointRow[]>([]);

  useEffect(() => {
    const orderId = row.trip?.orderId;
    if (!orderId) return;

    void api
      .orderHistory(orderId)
      .then((r) => setHistory(r.items))
      .catch(() => {});
    void api
      .orderTrack(orderId)
      .then((r) => setTrack(r.items))
      .catch(() => {});
  }, [row.trip?.orderId]);

  const t = row.trip;

  return (
    <>
      <dl className="specs">
        <div>
          <dt>Роли</dt>
          <dd>{row.roles.map((r) => COMPANY_ROLE_LABEL[r]).join(', ')}</dd>
        </div>
        <div>
          <dt>Машина</dt>
          <dd>{vehicle ? vehicleTitle(vehicle) : 'не закреплена'}</dd>
        </div>
        <div>
          <dt>Телефон</dt>
          <dd>
            {row.phone ? <a href={`tel:${row.phone}`}>{row.phone}</a> : 'не указан'}
          </dd>
        </div>
        <div>
          <dt>Telegram</dt>
          <dd>
            {row.tgUsername ? (
              <a href={`https://t.me/${row.tgUsername}`}>@{row.tgUsername}</a>
            ) : (
              'не привязан'
            )}
          </dd>
        </div>
        <div>
          <dt>Логин</dt>
          <dd>{row.login ?? '—'}</dd>
        </div>
        <div>
          <dt>Рейсов сделано</dt>
          <dd>{row.ordersDone}</dd>
        </div>
      </dl>

      {!t && (
        <div className="empty" style={{ marginTop: 12 }}>
          Сейчас без груза.
        </div>
      )}

      {t && (
        <>
          <h3 style={{ marginTop: 16 }}>Рейс № {t.number}</h3>
          <div className="route">
            <CountryFlag address={t.fromAddress} />
            {t.fromAddress}
            <Icon name="arrow" size={15} />
            <CountryFlag address={t.toAddress} />
            {t.toAddress}
          </div>
          <div className="meta">
            {t.title} · загрузка {new Date(t.loadDateFrom).toLocaleDateString('ru-RU')}
          </div>

          <div className="row" style={{ marginTop: 12 }}>
            <div>
              <div className="meta">Ставка</div>
              <div className="price">
                {t.price === null ? '—' : formatMoney(t.price, t.currency)}
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div className="meta">Расходы в рейсе</div>
              <strong>{formatRub(t.expensesRub)}</strong>
            </div>
          </div>

          <h3 style={{ marginTop: 16 }}>Где машина</h3>
          <TrackMap points={track} />

          <h3 style={{ marginTop: 16 }}>Статусы</h3>
          <ol className="timeline">
            {history.map((e) => (
              <li key={e.id} className={e.isRollback ? 'is-rollback' : ''}>
                <div>
                  <strong>{ORDER_STATUS_LABEL[e.toStatus] ?? e.toStatus}</strong>
                  <div className="meta">
                    {new Date(e.createdAt).toLocaleString('ru-RU')}
                    {e.byName ? ` · ${e.byName}` : ''}
                  </div>
                  {e.comment && <div className="meta">{e.comment}</div>}
                </div>
              </li>
            ))}
            {history.length === 0 && (
              <li>
                <div className="meta">Статусы ещё не менялись.</div>
              </li>
            )}
          </ol>
        </>
      )}
    </>
  );
}
